import earcut from "earcut";
import type { Position } from "geojson";
import { inject, injectable } from "inversify";
import { EventDispatcher, type IService, Logger, type XY } from "@designboticteam/core";
import { Kolega3DStoreService, SnapshotService, toClosedPolygon, toPlanar, type ValidationIssueKind, type ZoneSnapshot } from "@designboticteam/kolega-3d";
import { MapLibreInstanceService, ProjectionService } from "@designboticteam/maplibre";
import type * as Models from "../../schemas/models";
import {
	DEFAULT_BUILDING_LINE_PARAMETERS,
	DEFAULT_BUILDING_ZONE_PARAMETERS,
	DEFAULT_CHECK_ZONE_PARAMETERS,
	DEFAULT_EXCLUDED_ZONE_PARAMETERS,
	DEFAULT_PROJECT_DATA_PARAMETERS,
	ZERO_GUID,
} from "./constants";
import type { PlanDefinition } from "./types";
import { getCentroid, isPointInsidePolygon } from "./utils/polygon";
import { deepClone } from "./utils/struct";

const ISSUE_MESSAGES: Record<ValidationIssueKind, string> = {
	selfIntersection: "Shapes must not intersect themselves.",
	outsideProjectArea: "All shapes must be inside the project area.",
	outsideCheckZone: "All BuildingZones, ExcludedZones and BuildingLines must be inside a CheckZone.",
	noBuildingZoneInside: "Each CheckZone must contain a BuildingZone.",
};

@injectable()
class ExportService implements IService {
	public readonly serviceName = "ExportService";
	private readonly logger = new Logger(`⬆️ ${this.serviceName}`, { backgroundColor: "#ffffff", color: "#000000" });

	public readonly onExportRequested = new EventDispatcher("onExportRequested");
	public readonly onExportSucceeded = new EventDispatcher<{ payload: Models.Solution }>("onExportSucceeded");
	public readonly onExportFailed = new EventDispatcher<{ errors: Array<string> }>("onExportFailed");

	constructor(
		@inject(MapLibreInstanceService) private readonly mapLibreInstanceService: MapLibreInstanceService,
		@inject(SnapshotService) private readonly snapshotService: SnapshotService,
		@inject(ProjectionService) private readonly projectionService: ProjectionService,
		@inject(Kolega3DStoreService) private readonly kolega3DStoreService: Kolega3DStoreService,
	) {}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public update?(): void {
		this.logger.log("updated");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	/** */
	public async prepareExport(): Promise<void> {
		const projectArea = this.snapshotService.getSnapshot().projectAreaTool.projectArea;

		if (projectArea === null) {
			return;
		}

		const [x, , z] = projectArea.center;
		const { radius } = projectArea;

		const [swLng, swLat] = this.mapLibreInstanceService.sceneToWgs84({
			x: x - radius,
			z: z - radius,
		});
		const [neLng, neLat] = this.mapLibreInstanceService.sceneToWgs84({
			x: x + radius,
			z: z + radius,
		});

		this.mapLibreInstanceService.map.fitBounds(
			[
				[swLng, swLat],
				[neLng, neLat],
			],
			{ padding: 50, pitch: 0, bearing: 0, animate: false },
		);

		await new Promise<void>((resolve) => {
			this.mapLibreInstanceService.map.once("idle", () => resolve());
		});
	}

	public requestProjectAreaExport(): { hasErrors: false; payload: Models.Solution } | { hasErrors: true; errors: Array<string> } {
		const validationResult = this.getValidPlanDefinition();

		if (validationResult.hasErrors === true) {
			return validationResult;
		}

		const payload = this.buildPayload(validationResult.planDefinition);

		this.onExportSucceeded.dispatch({ payload });

		return { hasErrors: false, payload };
	}

	private getValidPlanDefinition(): { hasErrors: false; planDefinition: PlanDefinition } | { hasErrors: true; errors: Array<string> } {
		const {
			projectAreaTool: { projectArea },
			zoneTool: { zones },
			buildingLineTool: { buildingLines },
		} = this.snapshotService.getSnapshot();

		const errors = this.getValidationErrors();

		if (errors.length > 0 || projectArea === null) {
			console.warn({ errors });
			this.onExportFailed.dispatch({ errors });

			return { hasErrors: true, errors };
		}

		return {
			hasErrors: false,
			planDefinition: {
				projectArea,
				checkZones: zones.filter((zone) => zone.zoneType === "CheckZone"),
				buildingZones: zones.filter((zone) => zone.zoneType === "BuildingZone"),
				excludedZones: zones.filter((zone) => zone.zoneType === "ExcludedZone"),
				buildingLines,
			},
		};
	}

	private getValidationErrors(): Array<string> {
		const { validationStatus, validationIssues } = this.kolega3DStoreService.store.getState();
		const errors: Array<string> = [];

		if (validationStatus.isProjectAreaSet === false) {
			errors.push("Project center and radius must be set.");
		}

		if (validationStatus.hasCheckZone === false) {
			errors.push("At least one CheckZone is required.");
		}

		for (const kind of new Set(validationIssues.map((issue) => issue.kind))) {
			errors.push(ISSUE_MESSAGES[kind]);
		}

		return errors;
	}

	public buildPayload = (planDefinition: PlanDefinition): Models.Solution => {
		const { projectArea, checkZones, buildingZones, excludedZones, buildingLines } = planDefinition;
		const [x, , z] = projectArea.center;

		const projectCenterWgs84 = this.mapLibreInstanceService.sceneToWgs84({ x, z });
		const projectCenterPolandCs92 = this.projectionService.wgs84ToPolandCs92(projectCenterWgs84);

		return {
			// @ts-expect-error - Missing field in autogerated model
			SchemaVersion: "1.3",
			Input: {
				ProjectData: {
					...deepClone(DEFAULT_PROJECT_DATA_PARAMETERS),
					LocationOnEarth: {
						Longitude: projectCenterWgs84[0],
						Latitude: projectCenterWgs84[1],
					},
				},
				InputModel: {
					CheckZones: checkZones.map((checkZone) => ({
						...deepClone(DEFAULT_CHECK_ZONE_PARAMETERS),
						Guid: checkZone.id,
						Name: checkZone.zoneType,
						Boundary: {
							Points: this.transformToBoundaryPoints({
								points: toClosedPolygon(checkZone.points.map(toPlanar)) ?? [],
								projectCenterPolandCs92,
							}),
						},
					})),
					BuildingZones: buildingZones.map((buildingZone) => ({
						...deepClone(DEFAULT_BUILDING_ZONE_PARAMETERS),
						Guid: buildingZone.id,
						Name: buildingZone.zoneType,
						Boundary: {
							Points: this.transformToBoundaryPoints({
								points: toClosedPolygon(buildingZone.points.map(toPlanar)) ?? [],
								projectCenterPolandCs92,
							}),
						},
						ParentGuid: this.resolveParentGuid({ buildingZone, checkZones }),
					})),
					ExcludedZones: excludedZones.map((excludedZone) => ({
						...deepClone(DEFAULT_EXCLUDED_ZONE_PARAMETERS),
						Guid: excludedZone.id,
						Name: excludedZone.zoneType,
						Boundary: {
							Points: this.transformToBoundaryPoints({
								points: toClosedPolygon(excludedZone.points.map(toPlanar)) ?? [],
								projectCenterPolandCs92,
							}),
						},
					})),
					BuildingLines: buildingLines.map((buildingLine) => ({
						...deepClone(DEFAULT_BUILDING_LINE_PARAMETERS),
						Shape: {
							Points: this.transformToBoundaryPoints({
								points: buildingLine.points.map(toPlanar),
								projectCenterPolandCs92,
							}),
						},
						Guid: buildingLine.id,
					})),
					Surroundings: this.buildSurroundings({ projectCenterPolandCs92, radius: projectArea.radius }),
					NeighbourSamples: [],
				},
			},
			Guid: crypto.randomUUID(),
		};
	};

	private transformToBoundaryPoints({ points, projectCenterPolandCs92 }: { points: Array<XY>; projectCenterPolandCs92: XY }) {
		const projectedPolygon = points.map((point) => {
			const pointWgs84 = this.mapLibreInstanceService.sceneToWgs84({
				x: point[0],
				z: point[1],
			});
			const point2180 = this.projectionService.wgs84ToPolandCs92(pointWgs84);
			const relativePoint2180 = {
				X: point2180[0] - projectCenterPolandCs92[0],
				Y: point2180[1] - projectCenterPolandCs92[1],
				Z: 0,
			};

			return relativePoint2180;
		});

		return projectedPolygon;
	}

	private resolveParentGuid({ buildingZone, checkZones }: { buildingZone: ZoneSnapshot; checkZones: Array<ZoneSnapshot> }): string {
		const centroid = getCentroid(buildingZone.points.map(toPlanar));

		if (centroid === null) {
			return ZERO_GUID;
		}

		const parentZone = checkZones.find((checkZone) => isPointInsidePolygon(centroid, checkZone.points.map(toPlanar)));

		if (parentZone === undefined) {
			return ZERO_GUID;
		}

		return parentZone.id;
	}

	private buildSurroundings({ projectCenterPolandCs92, radius }: { projectCenterPolandCs92: XY; radius: number }): Array<Models.Surrounding> {
		const map = this.mapLibreInstanceService.map;
		const sourceCandidates = ["maptiler_planet", "openmaptiles", "composite"];
		const sourceId = sourceCandidates.find((id) => map.getSource(id));

		if (sourceId === undefined) {
			return [];
		}

		// Collect features polygons
		const polygons: Array<{ positions: Array<Position>; base: number; top: number }> = [];
		const features = map.querySourceFeatures(sourceId, { sourceLayer: "building" });

		for (const feature of features) {
			const { base, top } = this.resolveBuildingShape(feature.properties);

			if (feature.geometry.type === "Polygon") {
				const positions = feature.geometry.coordinates[0];
				polygons.push({ positions, base, top });
			} else if (feature.geometry.type === "MultiPolygon") {
				for (const polygon of feature.geometry.coordinates) {
					const positions = polygon[0];
					polygons.push({ positions, base, top });
				}
			}
		}
		//

		// Create mesh for each building footprint
		const surroundings: Array<Models.Surrounding> = [];

		for (const { positions, base, top } of polygons) {
			const footprint = positions.map(([x, y]) => this.projectionService.wgs84ToPolandCs92([x, y]));

			if (footprint.length < 3) {
				continue;
			}

			const centroid = getCentroid(footprint);

			if (centroid === null) {
				continue;
			}

			const distance = Math.hypot(centroid[0] - projectCenterPolandCs92[0], centroid[1] - projectCenterPolandCs92[1]);

			if (distance > radius) {
				continue;
			}

			const relativeFootprint = footprint.map(([x, y]) => [x - projectCenterPolandCs92[0], y - projectCenterPolandCs92[1]] satisfies XY);

			const mesh = this.createBuildingMesh({ footprint: relativeFootprint, base, top });

			surroundings.push({
				Name: "Surrounding",
				Mesh: mesh,
				Guid: crypto.randomUUID(),
				ParentGuid: ZERO_GUID,
			});
		}
		//

		return surroundings;
	}

	private createBuildingMesh({ footprint, base, top }: { footprint: Array<XY>; base: number; top: number }): Models.Mesh {
		const closedRing = toClosedPolygon(footprint);

		if (closedRing === null) {
			return { Vertices: [], Indices: [] };
		}

		const bottomVertices = closedRing.map(
			([x, y]) =>
				({
					X: x,
					Y: y,
					Z: base,
				}) satisfies Models.Point,
		);

		const topVertices = closedRing.map(
			([x, y]) =>
				({
					X: x,
					Y: y,
					Z: top,
				}) satisfies Models.Point,
		);

		const capIndices = earcut(closedRing.flat());
		const topOffset = bottomVertices.length;
		const indices: Array<number> = [];

		for (let i = 0; i < capIndices.length; i += 3) {
			indices.push(capIndices[i + 2], capIndices[i + 1], capIndices[i]);
		}

		for (let i = 0; i < capIndices.length; i += 3) {
			indices.push(topOffset + capIndices[i], topOffset + capIndices[i + 1], topOffset + capIndices[i + 2]);
		}

		for (let i = 0; i < closedRing.length - 1; i++) {
			const next = i + 1;
			const bottomCurrent = i;
			const bottomNext = next;
			const topCurrent = topOffset + i;
			const topNext = topOffset + next;

			indices.push(bottomCurrent, bottomNext, topNext);
			indices.push(bottomCurrent, topNext, topCurrent);
		}

		return { Vertices: [...bottomVertices, ...topVertices], Indices: indices };
	}

	private resolveBuildingShape(properties: Record<string, unknown>): { base: number; top: number } {
		const topCandidates = [properties.render_height, properties.height];
		let top = 10;

		for (const candidate of topCandidates) {
			const value = Number(candidate);
			if (Number.isFinite(value) && value > 0) {
				top = value;
				break;
			}
		}

		const baseValue = Number(properties.min_height);
		const base = Number.isFinite(baseValue) && baseValue >= 0 ? baseValue : 0;

		return { base, top };
	}
}

export { ExportService };
